import React, {useEffect, useMemo, useState} from 'react';
import { Flex, Popover, Select, Statistic, Table, TableColumnsType } from 'antd'
import {ReportsFilter} from "./index";
import {ColumnGroupType} from "antd/es/table";
import {DefaultOptionType} from "antd/es/select";
import {t} from "i18next";
import useSWR from "swr";
import {FactorPeriod, ReportFactorDetails} from "../../api/types";
import urls from "../../api/urls";
import {api} from "../../api/apiProvider";
import IconArrowDownSmall from "../../components/ui/icons/IconArrowDownSmall";
import {AllProductsReport} from "./AllProducts";
import dayjs from "dayjs";
import { useTranslation } from 'react-i18next'
import { CaretRightOutlined } from '@ant-design/icons'
import IndicatorsComparisonSettings from './IndicatorsComparisonSettings'
import { renderNumberCell } from '../../numberFormat'

const dateFormat = 'DD.MM.YYYY';

interface DataResourceType {
  id?: string;
  key?: string;
  number: string;
  indicator: string;
  channel: string;
}

interface IndicatorsComparisonProps {
  reportsFilter: ReportsFilter,
  selectedItem: AllProductsReport,
  setReportsFilter: Function,
}

type TableType = 'total' | 'year' | 'quarter'
const defaultType: TableType = 'total';

const getTypeName = (type: TableType) => {
  switch (type) {
    case 'quarter':
      return t('reports.quarter')

    case 'total':
      return t('reports.total')

    case 'year':
      return t('reports.year')
  }
};

const groups = [
  {
    value: 'kpi',
    label: t('planner.kpiInTotals'),
    fullLabel: t('planner.kpiInTotalsFull'),
  },
  {
    value: 'kpiShortTerm',
    label: t('planner.kpiShortTerm'),
    fullLabel: t('planner.kpiShortTermFull'),
  },
  {
    value: 'kpiLongTerm',
    label: t('planner.kpiLongTerm'),
    fullLabel: t('planner.kpiLongTermFull'),
  },
  {
    value: 'kpiFactorShare',
    label: t('planner.kpiFactorShare'),
    fullLabel: t('planner.kpiFactorShareFull'),
  },
  {
    value: 'kpiFactorShareShortTerm',
    label: t('planner.kpiFactorShareShortTerm'),
    fullLabel: t('planner.kpiFactorShareShortTermFull'),
  },
  {
    value: 'kpiFactorShareLongTerm',
    label: t('planner.kpiFactorShareLongTerm'),
    fullLabel: t('planner.kpiFactorShareLongTermFull'),
  },
  {
    value: 'budget',
    label: t('reports.budget'),
  },
];

export type KpiColumnsShow = 'kpi' | 'all';

const kpiColumnsToHide = [
  'kpiShortTerm', 'kpiLongTerm', 'kpiFactorShareShortTerm', 'kpiFactorShareLongTerm'
];

function createChannelRow(item: ReportFactorDetails, num: number) {
  const row: any = {
    number: num,
    channel: item.name || '',
    id: item['@id'],
    key: item['@id'],
  };

  fillNumericCells(row, item.periods);
  return row;
}

function createEmptyCells(report: ReportFactorDetails[]) {
  const empty: Record<string, JSX.Element> = {};
  for (const group of groups) {
    for (const period of report?.[0]?.periods || []) {
      const key = `${group.value}_${period.periodLabel}`;
      empty[key] = <Flex justify="end">-</Flex>;
    }
  }
  return empty;
}

function fillNumericCells(row: any, periods: FactorPeriod[]) {
  for (const group of groups) {
    for (const period of periods) {
      const key = `${group.value}_${period.periodLabel}`;
      const value = period[group.value as keyof FactorPeriod];

      row[key] = renderNumberCell(value);
    }
  }
}

export default function IndicatorsComparison({reportsFilter, selectedItem, setReportsFilter}: IndicatorsComparisonProps) {
  const [type, setType] = useState<TableType>(defaultType);
  const [report, setReport] = useState<ReportFactorDetails[]>([]);
  const [isNonMediaExpanded, setIsNonMediaExpanded] = useState<boolean>(false);
  const [kpiColumns, setKpiColumns] = useState<KpiColumnsShow>('kpi');
  const {t} = useTranslation();

  const typeOptions: DefaultOptionType[] = [
    {
      label: getTypeName('total'),
      value: 'total',
    },
    {
      label: getTypeName('year'),
      value: 'year',
    },
    {
      label: getTypeName('quarter'),
      value: 'quarter',
    },
  ];

  const getParamsString = () => {
    const params = new URLSearchParams([
      ['t[]', 'dict'],
      ['t[]', 'factor:channel'],
    ]);

    if (reportsFilter.channels) {
      for (let channelId of reportsFilter.channels) {
        params.append('filterChannels[]', String(channelId))
      }
    }

    if (reportsFilter.period && reportsFilter.period[0] && reportsFilter.period[1] && reportsFilter.period[0] instanceof dayjs && reportsFilter.period[1] instanceof dayjs) {
      params.append('historicDateEnd[after]', String(dayjs(reportsFilter.period[0], 'DD.MM.YYYY').format(dateFormat)));
      params.append('historicDateStart[before]', String(dayjs(reportsFilter.period[1], 'DD.MM.YYYY').format(dateFormat)))
    }

    params.append('period_type', type);

    return params.toString()
  };

  const {
    data: reportDetails,
    isValidating: isLoading,
  } = useSWR<{
    data: ReportFactorDetails[],
  }>(selectedItem ? (urls.engines + `/${selectedItem.engineId}/report_details?${getParamsString()}`) : null, api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    if (reportDetails && reportDetails.data) {
      setReport(reportDetails.data)
    }
  }, [reportDetails]);

  const defaultColumns: TableColumnsType<DataResourceType> = [
    {
      title: '',
      dataIndex: 'number',
      key: 'number',
      width: 40,
      fixed: 'left',
      children: [{
        title: '',
        dataIndex: 'number',
        key: 'number',
        width: 40,
        fixed: 'left',
      }],
    },
    {
      title: <Flex justify={'space-between'}>
        <span>{'Показатель'}</span>
        <Select
          options={typeOptions}
          size={'small'}
          value={{
            value: type,
            label: getTypeName(type)
          }}
          labelInValue
          onChange={(data: DefaultOptionType) => {
            setType(data.value as TableType)
          }}
          popupClassName={'indicator-select'}
          suffixIcon={<IconArrowDownSmall/>}
        />
      </Flex>,
      dataIndex: 'indicator',
      key: 'indicator',
      width: 220,
      fixed: 'left',
      children: [{
        title: 'Канал',
        dataIndex: 'channel',
        key: 'channel',
        width: 220,
        fixed: 'left',
      }],
    }
  ];

  const columns: TableColumnsType<DataResourceType> = useMemo(() => {
    if (!report || report.length === 0) {
      return defaultColumns
    }

    const items = defaultColumns;

    const reportItem: ReportFactorDetails = report[0];

    for (let groupItem of groups) {
      if (kpiColumns === 'kpi' && kpiColumnsToHide.includes(groupItem.value)) {
        continue;
      }

      const columnItem: ColumnGroupType<DataResourceType> = {
        title: groupItem.fullLabel ?
          <Popover placement="top" content={
          <div>{groupItem.fullLabel}</div>
        } arrow overlayClassName={'sider-links__popover'}>
            {groupItem.label}
        </Popover> : groupItem.label,
        key: groupItem.value,
        width: type === 'total' ? 300 : 180,
        children: [],
      };

      for (let period of reportItem.periods) {
        columnItem.children.push({
          title: period.periodLabel,
          dataIndex: groupItem.value + '_' + period.periodLabel,
          key: groupItem.value + '_' + period.periodLabel,
          width: type === 'total' ? 300 : 180,
        })
      }
      items.push(columnItem)
    }

    return items
  }, [report, kpiColumns]);

  const data = useMemo(() => {
    if (!report?.length) return [];

    const items: any[] = [];
    let number = 1;

    const mediaChannels = report.filter(item => item.isChannel === true || item.isChannel === null);

    mediaChannels.forEach(channel => {
      items.push(createChannelRow(channel, number++));
    });

    const nonMediaItems = report.filter(item => item.isChannel === false);
    const nonMediaCount = nonMediaItems.length;

    if (nonMediaCount > 0) {
      items.push({
        number: number++,
        channel: (
          <Flex
            justify="space-between"
            align="center"
            className={`data-total__group-title ${isNonMediaExpanded ? 'expanded' : ''}`}
            onClick={() => setIsNonMediaExpanded(!isNonMediaExpanded)}
          >
            <Flex>{t('planner.nonMedia')} ({nonMediaCount})</Flex>
            <CaretRightOutlined rotate={isNonMediaExpanded ? 90 : 0} style={{ color: '#7E8681' }} />
          </Flex>
        ),
        id: 'nonMedia',
        key: 'nonMedia',
        ...createEmptyCells(report),
      });

      if (isNonMediaExpanded) {
        nonMediaItems.forEach(channel => {
          items.push(createChannelRow(channel, number++));
        });
      }
    }

    return items;
  }, [report, groups, isNonMediaExpanded, t]);

  return (
    <>
      <IndicatorsComparisonSettings
        selectedChannelsIds={reportsFilter.channels || []}
        setSelectedChannelsIds={(data: string[]) => setReportsFilter({
          ...reportsFilter,
          channels: data,
        })}
        companies={reportsFilter?.companies || []}
        engineId={selectedItem.engineId}
        kpiColumns={kpiColumns}
        setKpiColumns={setKpiColumns}
      />
      <Table
        className={'briefs-table'}
        columns={columns}
        dataSource={data}
        rowKey={'id'}
        scroll={{x: 1000}}
        size={'middle'}
        loading={isLoading}
        pagination={false}
        bordered
        sticky
      />
    </>
  )
}
